import { Action } from 'redux';
import { ApiHeaderOpts } from 'services/api';

export const UPDATE_CITATION_VEHICLE = 'citations/UPDATE_CITATION_VEHICLE';
export interface UpdateCitationVehicleAction extends Action<typeof UPDATE_CITATION_VEHICLE> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly citation: Citation;
    readonly note?: string;
    readonly vehicle: CitedVehicle;
  };
}

export const updateCitationVehicle = (
  payload: UpdateCitationVehicleAction['payload'],
  meta: ActionMetaData = {},
): UpdateCitationVehicleAction => ({
  meta,
  payload,
  type: UPDATE_CITATION_VEHICLE,
});

export const DOWNGRADE_CITATION_FINE = 'citations/DOWNGRADE_CITATION_FINE';
export interface DowngradeCitationFineAction extends Action<typeof DOWNGRADE_CITATION_FINE> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly citation: Citation;
    readonly amount: number;
    readonly note?: string;
    readonly newLateFeeAmount?: string;
    readonly removeLateFee?: boolean;
  };
}

export const downgradeCitationFine = (
  payload: DowngradeCitationFineAction['payload'],
  meta: ActionMetaData = {},
): DowngradeCitationFineAction => ({
  meta,
  payload,
  type: DOWNGRADE_CITATION_FINE,
});

export const DOWNGRADE_CITATION_TO_WARNING = 'citations/DOWNGRADE_CITATION_TO_WARNING';
export interface DowngradeCitationToWarningAction extends Action<typeof DOWNGRADE_CITATION_TO_WARNING> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly citation: Citation;
    readonly note?: string;
  };
}

export const downgradeCitationToWarning = (
  payload: DowngradeCitationToWarningAction['payload'],
  meta: ActionMetaData = {},
): DowngradeCitationToWarningAction => ({
  meta,
  payload,
  type: DOWNGRADE_CITATION_TO_WARNING,
});

export const GET_CITATION_PAYMENT_AMOUNTS = 'citations/GET_CITATION_PAYMENT_AMOUNTS';
export interface GetCitationPaymentAmountsAction extends Action<typeof GET_CITATION_PAYMENT_AMOUNTS> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly citation: Citation;
  };
}

/**
 *  Used to get the calculated totals and discounts for making payments towards a citation
 * API
 */
export const getCitationPaymentAmounts = (citation: Citation, meta: ActionMetaData = {}) => ({
  meta,
  payload: {
    citation,
  },
  type: GET_CITATION_PAYMENT_AMOUNTS,
});

export const PERSIST_CITATION_PAYMENT_AMOUNTS = 'citations/PERSIST_CITATION_PAYMENT_AMOUNTS';
export interface PersistCitationPaymentAmountsAction extends Action<typeof PERSIST_CITATION_PAYMENT_AMOUNTS> {
  readonly payload: {
    readonly paymentAmounts: CitationPaymentAmount;
  };
}

export const persistCitationPaymentAmounts = (
  paymentAmounts: CitationPaymentAmount,
): PersistCitationPaymentAmountsAction => ({
  payload: {
    paymentAmounts,
  },
  type: PERSIST_CITATION_PAYMENT_AMOUNTS,
});

export const ACCEPT_CITATION_PAYMENT = 'citations/ACCEPT_CITATION_PAYMENT';
export interface AcceptCitationPaymentAction extends Action<typeof ACCEPT_CITATION_PAYMENT> {
  readonly meta: ActionMetaData;
  readonly payload: OTCCitationPaymentRequest;
}

export const acceptCitationPayment = (
  payment: OTCCitationPaymentRequest,
  meta: ActionMetaData = {},
): AcceptCitationPaymentAction => ({
  meta,
  payload: payment,
  type: ACCEPT_CITATION_PAYMENT,
});

export const ADJUST_CITATION_PAYMENT = 'citations/ADJUST_CITATION_PAYMENT';
export interface AdjustCitationPaymentAction extends Action<typeof ADJUST_CITATION_PAYMENT> {
  readonly meta: ActionMetaData;
  readonly payload: OTCCitationPaymentRequest;
}

export const adjustCitationPayment = (
  payment: OTCCitationPaymentRequest,
  meta: ActionMetaData = {},
): AdjustCitationPaymentAction => ({
  meta,
  payload: payment,
  type: ADJUST_CITATION_PAYMENT,
});

export const REVERSE_CITATION_PAYMENT = 'citations/REVERSE_CITATION_PAYMENT';
export interface ReverseCitationPaymentAction extends Action<typeof REVERSE_CITATION_PAYMENT> {
  readonly meta: ActionMetaData;
  readonly payload: CitationActionRequest;
}

export const reverseCitationPayment = (
  payload: CitationActionRequest,
  meta: ActionMetaData = {},
): ReverseCitationPaymentAction => ({
  meta,
  payload,
  type: REVERSE_CITATION_PAYMENT,
});

export const ADD_CITATION_IMAGE = 'citations/ADD_CITATION_IMAGE';
export interface AddCitationImageAction extends Action<typeof ADD_CITATION_IMAGE> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly file: File;
    readonly citationId: number;
  };
}

export const addCitationImage = (
  payload: AddCitationImageAction['payload'],
  meta: ActionMetaData = {},
): AddCitationImageAction => ({
  meta,
  payload,
  type: ADD_CITATION_IMAGE,
});

export const DELETE_CITATION_IMAGE = 'citations/DELETE_CITATION_IMAGE';
export interface DeleteCitationImageAction extends Action<typeof DELETE_CITATION_IMAGE> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly citation: Citation;
    readonly image: ImageFile;
  };
}

export const deleteCitationImage = (
  payload: DeleteCitationImageAction['payload'],
  meta: ActionMetaData = {},
): DeleteCitationImageAction => ({
  meta,
  payload,
  type: DELETE_CITATION_IMAGE,
});

export const START_CITATION = 'citations/START_CITATION';
export interface StartCitationAction extends Action<typeof START_CITATION> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly licensePlateNumber: string;
  };
}

export const startCitation = (licensePlate: string, meta: ActionMetaData = {}) => ({
  meta,
  payload: {
    licensePlateNumber: licensePlate,
  },
  type: START_CITATION,
});

export const PERSIST_PENDING_CITATIONS = 'citations/PERSIST_PENDING_CITATIONS';
export interface PersistPendingCitationsAction extends Action<typeof PERSIST_PENDING_CITATIONS> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly pendingCitations?: ReadonlyArray<PartialPendingCitation>;
  };
}

export const persistPendingCitations = (
  pendingCitations: ReadonlyArray<PartialPendingCitation>,
  meta: ActionMetaData = {},
): PersistPendingCitationsAction => ({
  meta,
  payload: {
    pendingCitations,
  },
  type: PERSIST_PENDING_CITATIONS,
});

export const ISSUE_CITATION = 'citations/ISSUE_CITATION';
export interface IssueCitationAction extends Action<typeof ISSUE_CITATION> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly citation: CreateCitationRequest;
  };
}

export const issueCitation = (citation: CreateCitationRequest, meta: ActionMetaData = {}) => ({
  meta,
  payload: {
    citation,
  },
  type: ISSUE_CITATION,
});

export const GET_CITATION = 'citations/GET_CITATION';
export interface GetCitationAction extends Action<typeof GET_CITATION> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly id: number;
  };
}

export const getCitation = (id: number, meta: ActionMetaData = {}): GetCitationAction => ({
  meta,
  payload: {
    id,
  },
  type: GET_CITATION,
});

export const PERSIST_CITATION = 'citations/PERSIST_CITATION';
export interface PersistCitationAction extends Action<typeof PERSIST_CITATION> {
  readonly payload: {
    readonly citation: Citation;
    readonly opts?: ApiHeaderOpts;
  };
}

export const persistCitation = (payload: PersistCitationAction['payload']): PersistCitationAction => ({
  payload,
  type: PERSIST_CITATION,
});

export const REINSTATE_CITATION = 'citations/REINSTATE_CITATION';
export interface ReinstateCitationAction extends Action<typeof REINSTATE_CITATION> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly citation: Citation;
    readonly note?: string;
  };
}
export const reinstateCitation = (
  payload: ReinstateCitationAction['payload'],
  meta: ActionMetaData = {},
): ReinstateCitationAction => ({
  meta: meta,
  payload,
  type: REINSTATE_CITATION,
});

export const RESEND_CITATION_ISSUANCE_EMAIL = 'citations/RESEND_CITATION_ISSUANCE_EMAIL';
export interface ResendCitationIssuanceEmailAction extends Action<typeof RESEND_CITATION_ISSUANCE_EMAIL> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly citation: Citation;
    /**
     * if this value is empty and the citation is for a known driver it will use
     * the original email. If unknown driver than no email will be sent
     */
    readonly email?: string;
    readonly note?: string;
  };
}

export const resendCitationIssuanceEmail = (
  payload: ResendCitationIssuanceEmailAction['payload'],
  meta: ActionMetaData = {},
): ResendCitationIssuanceEmailAction => ({
  meta,
  payload,
  type: RESEND_CITATION_ISSUANCE_EMAIL,
});

export const WAIVE_CITATION = 'citations/WAIVE_CITATION';
export interface WaiveCitationAction extends Action<typeof WAIVE_CITATION> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly citation: Citation;
    readonly note?: string;
  };
}

export const waiveCitation = (
  payload: WaiveCitationAction['payload'],
  meta: ActionMetaData = {},
): WaiveCitationAction => ({
  meta,
  payload,
  type: WAIVE_CITATION,
});

export const ADD_CITATION_NOTE = 'citations/ADD_CITATION_NOTE';
export interface AddCitationNoteAction extends Action<typeof ADD_CITATION_NOTE> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly citationId: number;
    readonly note: string;
  };
}

export const addCitationNote = (action: CitationActionRequest, meta: ActionMetaData = {}): AddCitationNoteAction => ({
  meta,
  payload: {
    citationId: action.citationId!,
    note: action.note!,
  },
  type: ADD_CITATION_NOTE,
});

export const GET_CITATION_AS_PDF = 'citations/GET_CITATION_AS_PDF';
export interface GetCitationAsPDFAction extends Action<typeof GET_CITATION_AS_PDF> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly citation: Citation;
    readonly includeNotes?: boolean;
    readonly includeAppeals?: boolean;
  };
}

export const getCitationAsPDF = (
  payload: GetCitationAsPDFAction['payload'],
  meta: ActionMetaData = {},
): GetCitationAsPDFAction => ({
  meta,
  payload,
  type: GET_CITATION_AS_PDF,
});

export const GET_CITATION_PAYMENT_RECEIPT_AS_PDF = 'citations/GET_CITATION_PAYMENT_RECEIPT_AS_PDF';
export interface GetCitationPaymentReceiptAsPDFAction extends Action<typeof GET_CITATION_PAYMENT_RECEIPT_AS_PDF> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly citation: Citation;
  };
}

export const getCitationPaymentReceiptAsPDF = (
  payload: GetCitationPaymentReceiptAsPDFAction['payload'],
  meta: ActionMetaData = {},
): GetCitationPaymentReceiptAsPDFAction => ({
  meta,
  payload,
  type: GET_CITATION_PAYMENT_RECEIPT_AS_PDF,
});

//
// --- Appeals ---

export const LOCK_APPEAL = 'citations/LOCK_APPEAL';
export interface LockAppealAction extends Action<typeof LOCK_APPEAL> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly citation: Citation;
    readonly note?: string;
  };
}

export const lockAppeal = (payload: LockAppealAction['payload'], meta: ActionMetaData = {}): LockAppealAction => ({
  meta,
  payload,
  type: LOCK_APPEAL,
});

export const UNLOCK_APPEAL = 'citations/UNLOCK_APPEAL';
export interface UnlockAppealAction extends Action<typeof UNLOCK_APPEAL> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly citation: Citation;
    readonly note?: string;
  };
}

export const unlockAppeal = (
  payload: UnlockAppealAction['payload'],
  meta: ActionMetaData = {},
): UnlockAppealAction => ({
  meta,
  payload,
  type: UNLOCK_APPEAL,
});

export const ADD_ADMIN_NOTE_TO_APPEAL = 'citations/ADD_ADMIN_NOTE_TO_APPEAL';
export interface AddAdminNoteToAppealAction extends Action<typeof ADD_ADMIN_NOTE_TO_APPEAL> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly citation: Citation;
    readonly note: string;
  };
}

export const addAdminNoteToAppeal = (
  payload: AddAdminNoteToAppealAction['payload'],
  meta: ActionMetaData = {},
): AddAdminNoteToAppealAction => ({
  meta,
  payload,
  type: ADD_ADMIN_NOTE_TO_APPEAL,
});

export const EDIT_ADMIN_NOTE_TO_APPEAL = 'citations/EDIT_ADMIN_NOTE_TO_APPEAL';
export interface EditAdminNoteToAppealAction extends Action<typeof EDIT_ADMIN_NOTE_TO_APPEAL> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly id: number | string;
    readonly note: string;
  };
}

export const editAdminNoteToAppeal = (
  payload: EditAdminNoteToAppealAction['payload'],
  meta: ActionMetaData = {},
): EditAdminNoteToAppealAction => ({
  meta,
  payload,
  type: EDIT_ADMIN_NOTE_TO_APPEAL,
});

export const SEND_APPEAL_REPLY = 'citations/SEND_APPEAL_REPLY';
export interface SendAppealReplyAction extends Action<typeof SEND_APPEAL_REPLY> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly citation: Citation;
    readonly fromEmail: string;
    readonly fromName: string;
    readonly subject: string;
    readonly body: string;
  };
}

export const sendAppealReply = (
  payload: SendAppealReplyAction['payload'],
  meta: ActionMetaData = {},
): SendAppealReplyAction => ({
  meta,
  payload,
  type: SEND_APPEAL_REPLY,
});

export const SET_APPEAL_HEARING_DATE = 'citations/SET_APPEAL_HEARING_DATE';
export interface SetAppealHearingDateAction extends Action<typeof SET_APPEAL_HEARING_DATE> {
  readonly meta: ActionMetaData;
  readonly payload: AppealHearingDateUpdateRequest;
}

export const setAppealHearingDate = (
  payload: SetAppealHearingDateAction['payload'],
  meta: ActionMetaData = {},
): SetAppealHearingDateAction => ({
  meta,
  payload,
  type: SET_APPEAL_HEARING_DATE,
});

// reinstate finalize decision start
export const REINSTATE_FINALIZE_DECISION = 'citations/REINSTATE_FINALIZE_DECISION';
export interface ReinstateFinalizeDecisionAction extends Action<typeof REINSTATE_FINALIZE_DECISION> {
  readonly meta: ActionMetaData;
  readonly payload: any;
}

export const reinstateFinalizeDecision = (
  payload: ReinstateFinalizeDecisionAction['payload'],
  meta: ActionMetaData = {},
): ReinstateFinalizeDecisionAction => ({
  meta,
  payload,
  type: REINSTATE_FINALIZE_DECISION,
});

// reinstate finalize decision end

export const FINALIZE_APPEAL_DECISION = 'citations/FINALIZE_APPEAL_DECISION';
export interface FinalizeAppealDecisionAction extends Action<typeof FINALIZE_APPEAL_DECISION> {
  readonly meta: ActionMetaData;
  readonly payload: CitationActionRequest;
}

export const finalizeAppealDecision = (
  payload: FinalizeAppealDecisionAction['payload'],
  meta: ActionMetaData = {},
): FinalizeAppealDecisionAction => ({
  meta,
  payload,
  type: FINALIZE_APPEAL_DECISION,
});

export const ADD_APPEAL_DECISION = 'citations/ADD_APPEAL_DECISION';
export interface AddAppealDecisionAction extends Action<typeof ADD_APPEAL_DECISION> {
  readonly meta: ActionMetaData;
  readonly payload: PendingAppealDecisionRequest;
}

export const addAppealDecision = (
  payload: AddAppealDecisionAction['payload'],
  meta: ActionMetaData = {},
): AddAppealDecisionAction => ({
  meta,
  payload,
  type: ADD_APPEAL_DECISION,
});

export const REJECT_APPEAL_DECISION = 'citations/REJECT_APPEAL_DECISION';
export interface RejectPendingDecisionAction extends Action<typeof REJECT_APPEAL_DECISION> {
  readonly meta: ActionMetaData;
  readonly payload: CitationActionRequest;
}

export const rejectPendingDecision = (
  payload: RejectPendingDecisionAction['payload'],
  meta: ActionMetaData = {},
): RejectPendingDecisionAction => ({
  meta,
  payload,
  type: REJECT_APPEAL_DECISION,
});

export const ALLOW_DRIVER_REPLIES = 'citations/ALLOW_DRIVER_REPLIES';
export interface AllowDriverRepliesAction extends Action<typeof ALLOW_DRIVER_REPLIES> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly citation: Citation;
    readonly allowDriverReplies: boolean;
    readonly note?: string;
  };
}

export const allowDriverReplies = (
  payload: AllowDriverRepliesAction['payload'],
  meta: ActionMetaData = {},
): AllowDriverRepliesAction => ({
  meta,
  payload,
  type: ALLOW_DRIVER_REPLIES,
});

export const LOOKUP_UNKNOWN_CITATIONS = 'citations/LOOKUP_UNKNOWN_CITATIONS';
export interface LookupUnknownCitationsAction extends Action<typeof LOOKUP_UNKNOWN_CITATIONS> {
  readonly meta: ActionMetaData;
  readonly payload: LookupUnknownCitationsRequest;
}

export const lookupUnknownCitations = (
  payload: LookupUnknownCitationsAction['payload'],
  meta: ActionMetaData = {},
): LookupUnknownCitationsAction => ({
  meta,
  payload,
  type: LOOKUP_UNKNOWN_CITATIONS,
});

export const PERSIST_UNKNOWN_CITATION_MATCH_RESULTS = 'citations/PERSIST_UNKNOWN_CITATION_MATCH_RESULTS';

export interface PersistUnknownCitationMatchResultsAction extends Action<
  typeof PERSIST_UNKNOWN_CITATION_MATCH_RESULTS
> {
  readonly payload: {
    results: ReadonlyArray<UnknownDriverVehicle>;
  };
}
export const persistUnknownCitationMatchResults = (
  results: ReadonlyArray<UnknownDriverVehicle>,
): PersistUnknownCitationMatchResultsAction => ({
  payload: { results },
  type: PERSIST_UNKNOWN_CITATION_MATCH_RESULTS,
});

export const MATCH_UNKNOWN_CITATIONS = 'citations/MATCH_UNKNOWN_CITATIONS';
export interface MatchUnknownCitationsAction extends Action<typeof MATCH_UNKNOWN_CITATIONS> {
  readonly meta: ActionMetaData;
  readonly payload: MatchUnknownCitationRequest;
}

export const matchUnknownCitations = (
  payload: MatchUnknownCitationsAction['payload'],
  meta: ActionMetaData = {},
): MatchUnknownCitationsAction => ({
  meta,
  payload,
  type: MATCH_UNKNOWN_CITATIONS,
});

export const PAIR_CITATION = 'citations/PAIR_CITATION';
export interface PairCitationAction extends Action<typeof PAIR_CITATION> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly citationId: number;
    readonly euaId: number;
    readonly deleteAppeals?: boolean;
    readonly sendIssuanceEmails?: boolean;
  };
}

export const pairCitation = (
  payload: PairCitationAction['payload'],
  meta: ActionMetaData = {},
): PairCitationAction => ({
  meta,
  payload,
  type: PAIR_CITATION,
});

export const UNPAIR_CITATION = 'citations/UNPAIR_CITATION';
export interface UnpairCitationAction extends Action<typeof UNPAIR_CITATION> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly citationId: number;
    readonly deleteAppeals: boolean;
  };
}

export const unpairCitation = (
  payload: UnpairCitationAction['payload'],
  meta: ActionMetaData = {},
): UnpairCitationAction => ({
  meta,
  payload,
  type: UNPAIR_CITATION,
});

export const LOOKUP_CITATIONS_FOR_COLLECTIONS = 'citations/LOOKUP_CITATIONS_FOR_COLLECTIONS';
export interface LookupCitationsForCollectionsAction extends Action<typeof LOOKUP_CITATIONS_FOR_COLLECTIONS> {
  readonly meta: ActionMetaData;
  readonly payload: LookupCitationsForCollectionsRequest;
}

export const lookupCitationsForCollections = (
  request: LookupCitationsForCollectionsRequest,
  meta: ActionMetaData = {},
): LookupCitationsForCollectionsAction => ({
  meta,
  payload: request,
  type: LOOKUP_CITATIONS_FOR_COLLECTIONS,
});

export const PERSIST_CITATIONS_FOR_COLLECTIONS = 'citations/PERSIST_CITATIONS_FOR_COLLECTIONS';
export interface PersistCitationsForCollectionsAction extends Action<typeof PERSIST_CITATIONS_FOR_COLLECTIONS> {
  readonly payload: {
    readonly lookupResult?: LookupResult;
  };
}

export const persistCitatonsForCollections = (lookupResult?: LookupResult): PersistCitationsForCollectionsAction => ({
  payload: {
    lookupResult,
  },
  type: PERSIST_CITATIONS_FOR_COLLECTIONS,
});

export const GENERATE_COLLECTION_LETTERS = 'citations/GENERATE_COLLECTION_LETTERS';
export interface GenerateCollectionLettersAction extends Action<typeof GENERATE_COLLECTION_LETTERS> {
  readonly meta: ActionMetaData;
  readonly payload: {
    readonly request: GenerateCollectionsLettersRequest;
    readonly templateId?: number;
  };
}

export const generateCollectionLetters = (
  payload: GenerateCollectionLettersAction['payload'],
  meta: ActionMetaData = {},
): GenerateCollectionLettersAction => ({
  meta,
  payload,
  type: GENERATE_COLLECTION_LETTERS,
});
// bulk payments
export const GET_BULK_PAYMENTS = 'citations/GET_BULK_PAYMENTS';
export const getBulkPayment = (data: SelectReportData) => ({
  type: GET_BULK_PAYMENTS,
  payload: data,
});
export const SET_BULK_PAYMENTS = 'citations/SET_BULK_PAYMENTS';
export const setBulkPayment = (data: any) => ({
  type: SET_BULK_PAYMENTS,
  payload: data,
});

export interface BulkPaymentAction extends Action<typeof SET_BULK_PAYMENTS> {
  readonly payload: any;
}
// bulk payments end

// bulk citation accept payments

export const GET_BULK_CITATION_CREATED = 'citations/GET_BULK_CITATION_CREATED';
export const getBulkCitationCreates = (data: BulkCitationPaymentsFormValues, onComplete?: () => void) => ({
  meta: {
    onComplete,
  },
  type: GET_BULK_CITATION_CREATED,
  payload: data,
});

export const SET_BULK_CITATION_CREATED = 'citations/SET_BULK_CITATION_CREATED';
export const setBulkCitationCreates = (data: BulkCitationPaymentsFormValues | undefined, onComplete?: () => void) => ({
  meta: {
    onComplete,
  },
  type: SET_BULK_CITATION_CREATED,
  payload: data,
});

export const SET_BULK_PAYMENT_RESPONSE = 'citation/SET_BULK_PAYMENT_RESPONSE';
export const setBulkPaymentResponse = (data: ReadonlyArray<BulkPaymentsResponse>) => ({
  type: SET_BULK_PAYMENT_RESPONSE,
  payload: data,
});

export interface BulkPaymentsAction extends Action<typeof SET_BULK_CITATION_CREATED>, OnCompleteAction {
  readonly payload: BulkCitationPaymentsFormValues | undefined;
}

export interface SetBulkPaymentResponseAction extends Action<typeof SET_BULK_PAYMENT_RESPONSE> {
  readonly payload: ReadonlyArray<BulkPaymentsResponse>;
}

export const GET_CITATION_EMAIL_LOGS = 'citations/GET_EMAIL_LOGS';
export interface GetCitationEmailLogsAction extends Action<typeof GET_CITATION_EMAIL_LOGS> {
  readonly meta: {
    readonly onComplete?: (emailLogs: ReadonlyArray<CitationEmailLog>) => void;
    readonly onError?: (error: unknown) => void;
  };
  readonly payload: {
    readonly citationId: number;
  };
}
export const getEmailLogs = (
  citationId: number,
  meta: GetCitationEmailLogsAction['meta'] = {},
): GetCitationEmailLogsAction => ({
  meta,
  payload: {
    citationId,
  },
  type: GET_CITATION_EMAIL_LOGS,
});

export type CitationAction =
  | GenerateCollectionLettersAction
  | LookupCitationsForCollectionsAction
  | PersistCitationsForCollectionsAction
  | PersistUnknownCitationMatchResultsAction
  | LookupUnknownCitationsAction
  | MatchUnknownCitationsAction
  | UpdateCitationVehicleAction
  | DowngradeCitationFineAction
  | DowngradeCitationToWarningAction
  | GetCitationPaymentAmountsAction
  | PersistCitationPaymentAmountsAction
  | AcceptCitationPaymentAction
  | AdjustCitationPaymentAction
  | ReverseCitationPaymentAction
  | AddCitationImageAction
  | DeleteCitationImageAction
  | StartCitationAction
  | PersistPendingCitationsAction
  | IssueCitationAction
  | GetCitationAction
  | PersistCitationAction
  | ReinstateCitationAction
  | ResendCitationIssuanceEmailAction
  | WaiveCitationAction
  | AddCitationNoteAction
  | GetCitationAsPDFAction
  | GetCitationPaymentReceiptAsPDFAction
  | LockAppealAction
  | UnlockAppealAction
  | AddAdminNoteToAppealAction
  | SendAppealReplyAction
  | BulkPaymentAction
  | BulkPaymentsAction
  | SetBulkPaymentResponseAction;
